import React from "react";
import { Spin, notification } from "antd";
import { useTranslation } from "react-i18next";
import { useGlobalContext } from "../../../contexts/global-context/useGlobalContext";
import { useMutation, useQueryClient } from "react-query";
import { editCompanyBranch } from "../../../service/client";
import { useBranchContext } from "../../../contexts/branch-context";
import {
  SectionTitle,
  StyledSwitch,
  SettingItem,
  SettingRow,
  SettingContent,
  SettingLabel,
  SettingDescription
} from "../../../styles/shared-components";

export const GlobalSettings: React.FC = () => {
  const { t } = useTranslation();
  const { setEnableOrderSound, enableOrderSound } = useGlobalContext();
  const { currentBranch } = useBranchContext();
  const queryClient = useQueryClient();

  const [dailySummaryEnabled, setDailySummaryEnabled] = React.useState<boolean>(false);
  const [monthlySummaryEnabled, setMonthlySummaryEnabled] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (currentBranch) {
      setDailySummaryEnabled(!!(currentBranch as any).dailySummaryEnabled);
      setMonthlySummaryEnabled(!!(currentBranch as any).monthlySummaryEnabled);
    }
  }, [currentBranch]);

  const { mutateAsync: updateBranch, isLoading: updatingBranch } = useMutation(
    editCompanyBranch,
    {
      onSuccess: () => {
        notification.success({
          message: t("globalNotifications.settingsUpdated"),
          placement: "bottomRight",
        });
        queryClient.invalidateQueries(["/company-branches"]);
      },
      onError: () => {
        notification.error({
          message: t("globalNotifications.error"),
          description: t("globalNotifications.settingsUpdateFailed"),
          placement: "bottomRight",
        });
      },
    }
  );

  if (!currentBranch) {
    return <Spin />;
  }

  return (
    <>
      <SectionTitle>{t("globalNotifications.branchNotifications")}</SectionTitle>

      <SettingItem>
        <SettingRow>
          <StyledSwitch
            onChange={(value) => {
              setEnableOrderSound(value);
            }}
            checked={enableOrderSound ?? false}
          />
          <SettingContent>
            <SettingLabel>{t("globalNotifications.soundNotifications")}</SettingLabel>
            <SettingDescription>
              {t("globalNotifications.soundNotificationsDescription")}
            </SettingDescription>
          </SettingContent>
        </SettingRow>
      </SettingItem>

      <SettingItem>
        <SettingRow>
          <StyledSwitch checked={true} disabled={true} />
          <SettingContent>
            <SettingLabel>{t("globalNotifications.branchEmailNotifications")}</SettingLabel>
            <SettingDescription>
              {t("globalNotifications.branchEmailNotificationsDescription")}
            </SettingDescription>
          </SettingContent>
        </SettingRow>
      </SettingItem>

      <SettingItem>
        <SettingRow>
          <StyledSwitch
            onChange={(value) => {
              updateBranch({
                id: currentBranch.id,
                data: {
                  companySmsNotificationsActive: value,
                },
              });
            }}
            checked={currentBranch.companySmsNotificationsActive}
            loading={updatingBranch}
            disabled={updatingBranch}
          />
          <SettingContent>
            <SettingLabel>{t("globalNotifications.branchSmsNotifications")}</SettingLabel>
            <SettingDescription>
              {t("globalNotifications.branchSmsNotificationsDescription")}
            </SettingDescription>
          </SettingContent>
        </SettingRow>
      </SettingItem>

      <SectionTitle style={{ marginTop: 32 }}>{t("globalNotifications.clientNotifications")}</SectionTitle>

      <SettingItem>
        <SettingRow>
          <StyledSwitch checked={true} disabled={true} />
          <SettingContent>
            <SettingLabel>{t("globalNotifications.clientEmailNotifications")}</SettingLabel>
            <SettingDescription>
              {t("globalNotifications.clientEmailNotificationsDescription")}
            </SettingDescription>
          </SettingContent>
        </SettingRow>
      </SettingItem>

      <SettingItem>
        <SettingRow>
          <StyledSwitch
            onChange={(value) => {
              updateBranch({
                id: currentBranch.id,
                data: {
                  clientsSmsNotificationsActive: value,
                },
              });
            }}
            checked={currentBranch.clientsSmsNotificationsActive}
            loading={updatingBranch}
            disabled={updatingBranch}
          />
          <SettingContent>
            <SettingLabel>{t("globalNotifications.clientSmsNotifications")}</SettingLabel>
            <SettingDescription>
              {t("globalNotifications.clientSmsNotificationsDescription")}
            </SettingDescription>
          </SettingContent>
        </SettingRow>
      </SettingItem>

      <SectionTitle style={{ marginTop: 32 }}>{t("globalNotifications.emailSummaries")}</SectionTitle>

      <SettingItem>
        <SettingRow>
          <StyledSwitch
            checked={dailySummaryEnabled}
            loading={updatingBranch}
            disabled={updatingBranch}
            onChange={(value) => {
              setDailySummaryEnabled(value);
              updateBranch({
                id: currentBranch.id,
                data: { dailySummaryEnabled: value },
              });
            }}
          />
          <SettingContent>
            <SettingLabel>{t("globalNotifications.dailySummary")}</SettingLabel>
            <SettingDescription>
              {t("globalNotifications.dailySummaryDescription")}
            </SettingDescription>
          </SettingContent>
        </SettingRow>
      </SettingItem>

      <SettingItem>
        <SettingRow>
          <StyledSwitch
            checked={monthlySummaryEnabled}
            loading={updatingBranch}
            disabled={updatingBranch}
            onChange={(value) => {
              setMonthlySummaryEnabled(value);
              updateBranch({
                id: currentBranch.id,
                data: { monthlySummaryEnabled: value },
              });
            }}
          />
          <SettingContent>
            <SettingLabel>{t("globalNotifications.monthlySummary")}</SettingLabel>
            <SettingDescription>
              {t("globalNotifications.monthlySummaryDescription")}
            </SettingDescription>
          </SettingContent>
        </SettingRow>
      </SettingItem>
    </>
  );
};